import React, { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { SearchRange } from "@/types/dto/search-filters/range-filter";
import { RangeEnum } from "@/config/unified-search-config";
import { InputAdornment, TextField, Box } from "@mui/material";
import FilterBox from "./filter-box";

interface RangeFilterProps {
  filterData: SearchRange,
  onChange?: (data: Record<string, string>) => void;
  currentUserPriceRange?: { PriceFrom?: string; PriceTo?: string };
  isLoading?: boolean;
}

const PriceRangeFilter: React.FC<RangeFilterProps> = ({ filterData, onChange, currentUserPriceRange, isLoading }) => {
  const t = useTranslations("SEARCH");

  const [minValue, setMinValue] = useState<string>(filterData.minValue || "");
  const [maxValue, setMaxValue] = useState<string>(filterData.maxValue || "");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [lastChangedField, setLastChangedField] = useState<"min" | "max" | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);

  const validateRange = (min: string, max: string, changedField: "min" | "max"): string => {
    if (!min.trim() || !max.trim()) return ""; // Empty values are valid

    const minNum = parseFloat(min);
    const maxNum = parseFloat(max);

    if (minNum > maxNum) {
      return changedField === "min"
        ? t("Min Error")
        : t("Max Error");
    }

    return "";
  };

  const handlePriceChange = () => {
    if (!onChange || !lastChangedField) return;

    const error = validateRange(minValue, maxValue, lastChangedField);
    setErrorMessage(error);
    setHasError(!!error);

    // Don't update if range is invalid
    if (error) return;

    const priceData: Record<string, string> = {
      PriceFrom: minValue.trim() || "",
      PriceTo: maxValue.trim() || ""
    };

    onChange(priceData);
  };

  // Update values when filterData changes
  useEffect(() => {
    setMinValue(filterData.minValue || "");
    setMaxValue(filterData.maxValue || "");
    setErrorMessage(""); // Clear errors when data updates
    setHasError(false);
  }, [filterData.minValue, filterData.maxValue, currentUserPriceRange]);

  const handleInputChange = (value: string, isMin: boolean) => {
    // Remove non-digit characters and limit to 5
    const numericValue = value.replace(/[^0-9]/g, "").slice(0, 5);
    const fieldType = isMin ? "min" : "max";

    if (isMin) {
      setMinValue(numericValue);
    } else {
      setMaxValue(numericValue);
    }

    setLastChangedField(fieldType);
    setErrorMessage(""); // Clear error when user starts typing
    setHasError(false);
  };

  return (
    <FilterBox name={t(RangeEnum.price_tl)} sx={{ display: "flex", justifyContent: "center" }}>
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        width: { xs: "286px", legacyDesktop: "100%" },
        paddingY: "1.6rem"
      }}>
        <TextField
          onBeforeInput={e => {
            if (!/^[0-9]$/.test(e.data ?? "")) {
              e.preventDefault();
            }
          }}
          value={minValue}
          label={t("Min")}
          name="minValue"
          disabled={isLoading}
          error={hasError && lastChangedField === "min"}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              startAdornment: <InputAdornment position="start"
                sx={{
                  color: "text.primary",
                  "&.MuiInputAdornment-root": { marginRight: "unset" },
                  "& .MuiTypography-root": { color: "text.primary" },
                  "& span": { color: "text.primary" }
                }}
              >
                $
              </InputAdornment>
            }
          }}
          type="text"
          sx={{
            display: "flex",
            height: "4.8rem",
            width: "9.2rem",
            borderRadius: "0.4rem 0.4rem 0 0",
            color: "text.primary",
            "& .MuiInputLabel-root": { color: "text.primary" }
          }}
          onChange={(e) => handleInputChange(e.target.value, true)}
          onBlur={handlePriceChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handlePriceChange();
            }
          }}
        />
        <TextField
          onBeforeInput={e => {
            if (!/^[0-9]$/.test(e.data ?? "")) {
              e.preventDefault();
            }
          }}
          value={maxValue}
          label={t("Max")}
          name="maxValue"
          disabled={isLoading}
          error={hasError && lastChangedField === "max"}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              startAdornment: <InputAdornment position="start"
                sx={{
                  color: "text.primary",
                  "&.MuiInputAdornment-root": { marginRight: "unset" },
                  "& .MuiTypography-root": { color: "text.primary" },
                  "& span": { color: "text.primary" }
                }}
              >
                $
              </InputAdornment>
            }
          }}
          type="text"
          sx={{
            display: "flex",
            height: "4.8rem",
            width: "9.2rem",
            borderRadius: "0.4rem 0.4rem 0 0",
            color: "text.primary",
            "& .MuiInputLabel-root": { color: "text.primary" }
          }}
          onChange={(e) => handleInputChange(e.target.value, false)}
          onBlur={handlePriceChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handlePriceChange();
            }
          }}
        />
      </Box>
      {errorMessage && (
        <Box sx={{
          color: "error.main",
          fontSize: "1.2rem",
          fontWeight: 500,
          mt: 2,
          textAlign: "center"
        }}>
          {errorMessage}
        </Box>
      )}
    </FilterBox>
  );
};

export default PriceRangeFilter;